import type { CalendarEvent } from './calendarEventsApi';
import { logCalendarDebug } from './calendarEventsDebug';

/** TTL for soft snapshot after an interviewer is removed from the roster (ms). */
export const REMOVED_INTERVIEWER_SOFT_CACHE_TTL_MS = 5 * 60 * 1000;

type Entry = {
  events: CalendarEvent[];
  expiresAt: number;
};

const store = new Map<string, Entry>();

function cacheKey(userId: number, weekStartKey: string): string {
  return `${userId}:${weekStartKey}`;
}

export function stashRemovedInterviewerEvents(
  userId: number,
  weekStartKey: string,
  events: CalendarEvent[],
  ttlMs: number
): void {
  const k = cacheKey(userId, weekStartKey);
  store.set(k, {
    events,
    expiresAt: Date.now() + ttlMs,
  });
  logCalendarDebug('soft-cache STASH', {
    key: k,
    eventCount: events.length,
    ttlMs,
    expiresAtIso: new Date(Date.now() + ttlMs).toISOString(),
  });
}

export function takeSoftCachedInterviewerEvents(
  userId: number,
  weekStartKey: string
): CalendarEvent[] | null {
  const k = cacheKey(userId, weekStartKey);
  const entry = store.get(k);
  if (!entry) {
    return null;
  }
  if (Date.now() > entry.expiresAt) {
    store.delete(k);
    logCalendarDebug('soft-cache EXPIRED', { key: k });
    return null;
  }
  return entry.events;
}

export function clearSoftInterviewerCacheForUser(userId: number): void {
  const prefix = `${userId}:`;
  const keys = Array.from(store.keys());
  for (const k of keys) {
    if (k.startsWith(prefix)) {
      store.delete(k);
    }
  }
}

export function clearSoftInterviewerCacheEntry(
  userId: number,
  weekStartKey: string
): boolean {
  const k = cacheKey(userId, weekStartKey);
  if (!store.has(k)) {
    return false;
  }
  store.delete(k);
  logCalendarDebug('soft-cache CLEAR (server success)', { key: k });
  return true;
}
